import React, { useEffect, useState } from 'react'
import styled from 'styled-components';
import { maxMobile, maxWidth, primaryHeadingColor, retina, retinaOne, SecondaryColor, secondaryFont, wMBold, wMedium } from '../../../Utils/utilsStyle';
import GlobalButton from '../../common/components/GlobalButton';
import InfoIconTwo from '../../common/assets/svgIcons/InfoIconTwo';
import AccountClusterCard from '../../common/components/AccountClusterCard';
import { Col, Row } from 'antd';
import DrawerAccountCluster from './DrawerAccountCluster';
import { useDispatch, useSelector } from 'react-redux';
import { actions as clusterActions } from '../../../Slice/ClustersSlice';
import {
  selectActiveClusters,
  selectActiveClustersLoaded,
  selectClusterCount,
  selectClusterDrawerOpen,
  selecteActiveClusterLoading,
} from '../../../Selector/ClustersSelector';
import { Clusters } from '../../../Types/ClustersTypes';
import BuyerGroupSkeleton from '../../common/skeleton/BuyerGroupSkeleton';

const ACTIVE_CLUSTER_SKELETON_COUNT = 4;

const Wrapper = styled.div`
  .fancy-box {
    border-radius: 22px;
    background: rgb(255, 255, 255);
    padding: 40px 50px 40px 50px;
    margin-top: 50px;
    @media all and (max-width: ${maxWidth}) , ${retina} {
      padding: 17px 25px 25px 25px;
    }
    @media all and (max-width: ${maxWidth}) , ${retinaOne} {
      padding: 17px 25px 25px 25px;
    }
    @media all and (max-width: ${maxMobile}) {
      padding: 17px 16px 16px 16px;
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 16px;
      .left-side {
        display: flex;
        align-items: center;
        justify-content: space-between;
        column-gap: 8px;
        h1 {
          color: ${primaryHeadingColor};
          font-family: ${secondaryFont};
          font-size: 24px;
          font-weight: ${wMedium};
          line-height: 30px;
          letter-spacing: -0.4px;
          margin-bottom: 0px;
        }
      }
      .right-side {
        display: flex;
        align-items: center;
        justify-content: space-between;
        column-gap: 8px;
        .browse-clusters-button{
          font-weight: ${wMBold};
        }
      }
    }
    .empty {
      height: 223px;
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      row-gap: 8px;
      .heading {
        color: #0D0D0D;
        text-align: center;
        font-family: "Roboto Slab";
        font-size: 20px;
        font-style: normal;
        font-weight: 500;
        line-height: 30px;
        letter-spacing: -0.4px;
      }
      .sub-heading {
        color: #667982;
        font-family: Roboto;
        font-size: 14px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        letter-spacing: -0.4px;
      }
    }
  }
`;

const AccountClustersSection = function AccountClustersSection() {
  const dispatch = useDispatch();
  const [drawerMode, setDrawerMode] = useState<'create' | 'browse'>('browse');
  const activeClusters: Clusters[] = useSelector(selectActiveClusters);
  const clustersLoading = useSelector(selecteActiveClusterLoading);
  const activeClustersLoaded = useSelector(selectActiveClustersLoaded);
  const clusterCount = useSelector(selectClusterCount);
  const clusterDrawerOpen = useSelector(selectClusterDrawerOpen);

  const showSectionLoading = !activeClustersLoaded || clustersLoading;

  const openBrowseDrawer = () => {
    setDrawerMode('browse');
    dispatch(clusterActions.setDrawerOpen(true));
  };

  const openCreateDrawer = () => {
    setDrawerMode('create');
    dispatch(clusterActions.setDrawerOpen(true));
  };

  const toggleDrawer = () => {
    dispatch(clusterActions.setDrawerOpen(!clusterDrawerOpen));
  };

  useEffect(() => {
    if (!clusterDrawerOpen) {
      dispatch(clusterActions.setSelectedClusters([]));
    }
  }, [clusterDrawerOpen, dispatch]);

  useEffect(() => {
    dispatch(clusterActions.getAccountClusters({
      page: 1,
      type: 'active',
    }));
  }, [dispatch]);

  return (
    <Wrapper>
      <div className="fancy-box">
        <div className="header">
          <div className="left-side">
            <h1>
              Account Clusters
              {activeClustersLoaded ? ` (${clusterCount})` : ''}
            </h1>
            <span><InfoIconTwo /></span>
          </div>
          <div className="right-side">
            <GlobalButton
              title="Browse all clusters"
              textColor={SecondaryColor}
              fontSize="14px"
              bgColor="#fff"
              outlineColor={SecondaryColor}
              innerSpace="9px 10px"
              borderRadius="6px"
              buttonClassName="browse-clusters-button"
              onButtonClick={() => { openBrowseDrawer(); }}
              disabled={showSectionLoading || activeClusters.length === 0}
            />
            <GlobalButton
              title="Create New"
              textColor="#fff"
              outlineColor={SecondaryColor}
              bgColor={SecondaryColor}
              innerSpace="9px 10px"
              borderRadius="6px"
              fontSize="14px"
              buttonClassName="create-clusters-button"
              onButtonClick={() => { openCreateDrawer(); }}
            />
          </div>
        </div>
        {showSectionLoading ? (
          <div className="card-section">
            <Row gutter={[12, 12]} className="card-row">
              {Array.from({ length: ACTIVE_CLUSTER_SKELETON_COUNT }, (_, index) => (
                <Col
                  key={`cluster-skeleton-${index}`}
                  lg={{ span: 6 }}
                  xs={{ span: 24 }}
                  sm={{ span: 12 }}
                  className="tablet-col"
                >
                  <BuyerGroupSkeleton border />
                </Col>
              ))}
            </Row>
          </div>
        ) : activeClusters.length > 0 ? (
          <div className="card-section">
            <Row gutter={[12, 12]} className="card-row">
              {activeClusters.slice(0, ACTIVE_CLUSTER_SKELETON_COUNT).map((item) => (
                <Col
                  key={item.clusterId}
                  lg={{ span: 6 }}
                  xs={{ span: 24 }}
                  sm={{ span: 12 }}
                  className="tablet-col"
                >
                  <AccountClusterCard
                    clusterName={item?.clusterName}
                    clusterId={item?.clusterId}
                    companies={item?.companiesData}
                    createdBy={item?.ownerData}
                    clusterPersona={item?.personality}
                    sharedBy={item?.sharedByData}
                    isShared={item?.sharedByData !== null}
                  />
                </Col>
              ))}
            </Row>
          </div>
        ) : (
          <div className="empty">
            <div className="heading">No Clusters Found</div>
            <div className="sub-heading">
              Create an account cluster to begin organizing your accounts.
            </div>
          </div>
        )}
      </div>
      <DrawerAccountCluster
        toggleDrawer={toggleDrawer}
        mode={drawerMode}
      />
    </Wrapper>
  );
};

export default AccountClustersSection;
